import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Users, Clock, Shield, LogOut, BarChart3, TrendingUp } from 'lucide-react';
import observabilityService from '../../services/observabilityService';
import ConfirmDialog from '../common/ConfirmDialog';
import Notification from '../common/Notification';
import useNotification from '../../hooks/useNotification';

const StatCard = ({ label, value, icon: Icon, color, loading }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
  };
  const colors = colorMap[color] || colorMap.blue;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-full ${colors.bg}`}>
          <Icon className={`h-6 w-6 ${colors.text}`} />
        </div>
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="h-8 bg-gray-200 rounded animate-pulse w-16" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
        </div>
      ) : (
        <>
          <p className="text-3xl font-bold text-gray-900">
            {value != null ? value.toLocaleString() : '--'}
          </p>
          <p className="text-sm text-gray-500 mt-1">{label}</p>
        </>
      )}
    </div>
  );
};

const SectionError = ({ message }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
    <p className="text-sm text-red-600">{message}</p>
  </div>
);

const LoadingSkeleton = ({ rows = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
    ))}
  </div>
);

const formatTimestamp = (ts) => {
  if (!ts) return '--';
  try {
    return format(parseISO(ts), 'MMM d, yyyy h:mm a');
  } catch {
    return ts;
  }
};

const TREND_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

const UsersTab = ({ selectedFirmId }) => {
  const { notification, showError, hideNotification } = useNotification();
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  // DAU/WAU/MAU state
  const [dauData, setDauData] = useState(null);
  const [dauLoading, setDauLoading] = useState(true);
  const [dauError, setDauError] = useState(null);

  // Login history state
  const [loginHistory, setLoginHistory] = useState(null);
  const [loginPage, setLoginPage] = useState(1);
  const [loginLoading, setLoginLoading] = useState(true);
  const [loginError, setLoginError] = useState(null);

  // Last activity state
  const [lastActivity, setLastActivity] = useState(null);
  const [activityPage, setActivityPage] = useState(1);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState(null);

  // Sessions state
  const [sessions, setSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState(null);
  const [logoutInProgress, setLogoutInProgress] = useState(null);
  const [logoutSuccess, setLogoutSuccess] = useState(null);

  // Feature usage state
  const [featureUsage, setFeatureUsage] = useState(null);
  const [featureUsageLoading, setFeatureUsageLoading] = useState(true);
  const [featureUsageError, setFeatureUsageError] = useState(null);

  // Feature trends state
  const [featureTrends, setFeatureTrends] = useState(null);
  const [featureTrendsLoading, setFeatureTrendsLoading] = useState(true);
  const [featureTrendsError, setFeatureTrendsError] = useState(null);

  // Fetch DAU/WAU/MAU, sessions, and feature data on firmId change
  const fetchCoreData = useCallback(async () => {
    setDauLoading(true);
    setDauError(null);
    setSessionsLoading(true);
    setSessionsError(null);
    setFeatureUsageLoading(true);
    setFeatureUsageError(null);
    setFeatureTrendsLoading(true);
    setFeatureTrendsError(null);

    try {
      const data = await observabilityService.getDauWauMau(selectedFirmId);
      setDauData(data);
    } catch (err) {
      console.error('Error fetching DAU/WAU/MAU:', err);
      setDauError('Failed to load active user metrics');
    } finally {
      setDauLoading(false);
    }

    try {
      const data = await observabilityService.getSessions(selectedFirmId);
      setSessions(data);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setSessionsError('Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }

    try {
      const data = await observabilityService.getFeatureUsage(selectedFirmId);
      setFeatureUsage(data);
    } catch (err) {
      console.error('Error fetching feature usage:', err);
      setFeatureUsageError('Failed to load feature usage');
    } finally {
      setFeatureUsageLoading(false);
    }

    try {
      const data = await observabilityService.getFeatureTrends(selectedFirmId);
      setFeatureTrends(data);
    } catch (err) {
      console.error('Error fetching feature trends:', err);
      setFeatureTrendsError('Failed to load feature trends');
    } finally {
      setFeatureTrendsLoading(false);
    }
  }, [selectedFirmId]);

  useEffect(() => {
    fetchCoreData();
  }, [fetchCoreData]);

  // Fetch login history (depends on page and firmId)
  useEffect(() => {
    let cancelled = false;
    const fetchLoginHistory = async () => {
      setLoginLoading(true);
      setLoginError(null);
      try {
        const data = await observabilityService.getLoginHistory(selectedFirmId, loginPage, 25);
        if (!cancelled) setLoginHistory(data);
      } catch (err) {
        console.error('Error fetching login history:', err);
        if (!cancelled) setLoginError('Failed to load login history');
      } finally {
        if (!cancelled) setLoginLoading(false);
      }
    };
    fetchLoginHistory();
    return () => { cancelled = true; };
  }, [selectedFirmId, loginPage]);

  // Reset login page when firm changes
  useEffect(() => {
    setLoginPage(1);
  }, [selectedFirmId]);

  // Fetch last activity (depends on page and firmId)
  useEffect(() => {
    let cancelled = false;
    const fetchLastActivity = async () => {
      setActivityLoading(true);
      setActivityError(null);
      try {
        const data = await observabilityService.getUserLastActivity(selectedFirmId, activityPage, 50);
        if (!cancelled) setLastActivity(data);
      } catch (err) {
        console.error('Error fetching last activity:', err);
        if (!cancelled) setActivityError('Failed to load user activity');
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    };
    fetchLastActivity();
    return () => { cancelled = true; };
  }, [selectedFirmId, activityPage]);

  // Reset activity page when firm changes
  useEffect(() => {
    setActivityPage(1);
  }, [selectedFirmId]);

  // Force logout handler
  const handleForceLogout = (userId, userName) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Force Logout',
      message: `Force logout ${userName}? Their current session will be invalidated.`,
      variant: 'warning',
      confirmText: 'Force Logout',
      onConfirm: async () => {
        setLogoutInProgress(userId);
        setLogoutSuccess(null);
        try {
          await observabilityService.forceLogout(userId);
          setLogoutSuccess(userId);
          // Refetch sessions
          const data = await observabilityService.getSessions(selectedFirmId);
          setSessions(data);
          // Clear success after 3 seconds
          setTimeout(() => setLogoutSuccess(null), 3000);
        } catch (err) {
          console.error('Error forcing logout:', err);
          showError('Failed to force logout user. Please try again.');
        } finally {
          setLogoutInProgress(null);
        }
      },
    });
  };

  const loginTotalPages = loginHistory ? Math.ceil(loginHistory.total / 25) : 0;
  const activityTotalPages = lastActivity ? Math.ceil(lastActivity.total / 50) : 0;

  return (
    <>
    <Notification {...notification} onClose={hideNotification} />
    <ConfirmDialog
      isOpen={confirmDialog.isOpen}
      onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      onConfirm={confirmDialog.onConfirm}
      title={confirmDialog.title}
      message={confirmDialog.message}
      variant={confirmDialog.variant}
      confirmText={confirmDialog.confirmText}
    />
    <div className="space-y-6">
      {/* Section 1: Active Users (DAU/WAU/MAU) */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Users</h3>
        {dauError ? (
          <SectionError message={dauError} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <StatCard
                label="Daily Active Users"
                value={dauData?.dau}
                icon={Users}
                color="blue"
                loading={dauLoading}
              />
              <StatCard
                label="Weekly Active Users"
                value={dauData?.wau}
                icon={Users}
                color="green"
                loading={dauLoading}
              />
              <StatCard
                label="Monthly Active Users"
                value={dauData?.mau}
                icon={Users}
                color="yellow"
                loading={dauLoading}
              />
            </div>
            {dauLoading ? (
              <div className="h-64 bg-gray-200 rounded animate-pulse" />
            ) : dauData?.trend && dauData.trend.length > 0 ? (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">30-Day Active User Trend</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={dauData.trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => {
                        try { return format(parseISO(d), 'MMM d'); } catch { return d; }
                      }}
                      fontSize={12}
                    />
                    <YAxis fontSize={12} />
                    <Tooltip
                      labelFormatter={(d) => {
                        try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="active_users"
                      stroke="#3b82f6"
                      fill="#93c5fd"
                      name="Active Users"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No trend data available</p>
            )}
          </>
        )}
      </div>

      {/* Section 2: Login History */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Login History</h3>
        {loginError ? (
          <SectionError message={loginError} />
        ) : loginLoading ? (
          <LoadingSkeleton rows={6} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auth Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loginHistory?.items?.length > 0 ? (
                    loginHistory.items.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {item.first_name} {item.last_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{item.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatTimestamp(item.login_timestamp)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{item.ip_address || '--'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{item.auth_method || '--'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate" title={item.user_agent}>
                          {item.user_agent || '--'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                        No login history found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {loginTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <button
                  onClick={() => setLoginPage((p) => Math.max(1, p - 1))}
                  disabled={loginPage <= 1}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {loginPage} of {loginTotalPages}
                </span>
                <button
                  onClick={() => setLoginPage((p) => Math.min(loginTotalPages, p + 1))}
                  disabled={loginPage >= loginTotalPages}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Section 3: User Last Activity */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <Clock className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">User Last Activity</h3>
        </div>
        {activityError ? (
          <SectionError message={activityError} />
        ) : activityLoading ? (
          <LoadingSkeleton rows={6} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lastActivity?.items?.length > 0 ? (
                    lastActivity.items.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {item.first_name} {item.last_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{item.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatTimestamp(item.last_activity)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatTimestamp(item.last_login)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                        No activity data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {activityTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <button
                  onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                  disabled={activityPage <= 1}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {activityPage} of {activityTotalPages}
                </span>
                <button
                  onClick={() => setActivityPage((p) => Math.min(activityTotalPages, p + 1))}
                  disabled={activityPage >= activityTotalPages}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Section 4: Active Sessions with Force Logout */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <Shield className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Active Sessions</h3>
        </div>
        {sessionsError ? (
          <SectionError message={sessionsError} />
        ) : sessionsLoading ? (
          <LoadingSkeleton rows={5} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Seen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Browser</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OS</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions?.sessions?.length > 0 ? (
                    sessions.sessions.map((session, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {session.first_name} {session.last_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{session.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatTimestamp(session.last_seen)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{session.browser || '--'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{session.os || '--'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{session.device || '--'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{session.ip_address || '--'}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {logoutSuccess === session.user_id ? (
                            <span className="text-green-600 text-sm font-medium">Logged out</span>
                          ) : (
                            <button
                              onClick={() =>
                                handleForceLogout(
                                  session.user_id,
                                  `${session.first_name} ${session.last_name}`
                                )
                              }
                              disabled={logoutInProgress === session.user_id}
                              className="inline-flex items-center px-2 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <LogOut className="h-3 w-3 mr-1" />
                              {logoutInProgress === session.user_id ? 'Logging out...' : 'Force Logout'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                        No active sessions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-400">
              Force Logout invalidates the user&apos;s current session. They will need to log in again.
            </p>
          </>
        )}
      </div>

      {/* Section 5: Feature Usage */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <BarChart3 className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Feature Usage</h3>
        </div>
        {featureUsageError ? (
          <SectionError message={featureUsageError} />
        ) : featureUsageLoading ? (
          <div className="h-64 bg-gray-200 rounded animate-pulse" />
        ) : featureUsage?.features?.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={Math.max(300, featureUsage.features.length * 40)}>
              <BarChart
                data={featureUsage.features}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="display_name"
                  fontSize={12}
                  width={90}
                />
                <Tooltip />
                <Bar dataKey="count" name="Usage Count">
                  {featureUsage.features.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.is_low_adoption ? '#f97316' : '#3b82f6'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-blue-500" />
                <span>Normal adoption</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-orange-500" />
                <span>Low adoption (bottom 20%)</span>
              </div>
              <span className="text-gray-400">Period: {featureUsage.period_days} days</span>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">No feature usage data available</p>
        )}
      </div>

      {/* Section 6: Feature Trends */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <TrendingUp className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Feature Adoption Trends</h3>
        </div>
        {featureTrendsError ? (
          <SectionError message={featureTrendsError} />
        ) : featureTrendsLoading ? (
          <div className="h-64 bg-gray-200 rounded animate-pulse" />
        ) : featureTrends?.features?.length > 0 ? (
          (() => {
            // Transform data: create array of {date, feature1: count, feature2: count, ...}
            const dateMap = {};
            featureTrends.features.forEach((feat) => {
              feat.data.forEach((point) => {
                if (!dateMap[point.date]) dateMap[point.date] = { date: point.date };
                dateMap[point.date][feat.display_name] = point.count;
              });
            });
            const chartData = Object.values(dateMap).sort((a, b) =>
              a.date.localeCompare(b.date)
            );

            return (
              <>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => {
                        try { return format(parseISO(d), 'MMM d'); } catch { return d; }
                      }}
                      fontSize={12}
                    />
                    <YAxis fontSize={12} />
                    <Tooltip
                      labelFormatter={(d) => {
                        try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
                      }}
                    />
                    <Legend />
                    {featureTrends.features.map((feat, idx) => (
                      <Line
                        key={feat.feature}
                        type="monotone"
                        dataKey={feat.display_name}
                        stroke={TREND_COLORS[idx % TREND_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <p className="mt-2 text-xs text-gray-400">
                  Period: {featureTrends.period_days} days
                </p>
              </>
            );
          })()
        ) : (
          <p className="text-sm text-gray-500">No feature trend data available</p>
        )}
      </div>
    </div>
    </>
  );
};

export default UsersTab;
